import React, { useEffect, useState } from 'react';
import {
  BadgeAnchor,
  Button,
  CloseIcon,
  ErrorOutlineIcon,
  MenuIcon,
  NotificationsIcon,
  PanelLeftIcon,
  PersonIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  Tooltip as DSTooltip,
  TopBar,
  TopBarButton,
  UploadFileIcon,
} from '@billda/design-system';
import { UserAvatar } from '../atoms/UserAvatar';
import { useShallow } from 'zustand/react/shallow';
import { UserMenuPopover } from '../../../features/auth/components/UserMenuPopover';
import { useCurrentUser } from '../../../features/auth/hooks/useCurrentUser';
import { useShellStore } from '../../stores/shellStore';
import { useUploadTrayStore } from '../../../features/sites/stores/uploadTrayStore';
import { UploadTrayItem } from '../../../features/sites/components/files/UploadTrayItem';
import { useLocation } from 'react-router-dom';
import { findActiveNavItem } from './navigationItems';

export const APPBAR_HEIGHT = 52;

/**
 * MUI-free media query — replaces @mui/material's useMediaQuery for the bar
 * chrome so the shell file only keeps MUI for the UploadIndicator internals.
 * Queries mirror MUI's default breakpoints (sm 600, md 960, lg 1024).
 */
function useMediaQueryMatches(query: string): boolean {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches
  );

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const handleChange = () => {
      setMatches(mediaQueryList.matches);
    };
    handleChange();
    mediaQueryList.addEventListener('change', handleChange);
    return () => {
      mediaQueryList.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

const UploadIndicator: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const items = useUploadTrayStore(state => state.items);
  const isExpanded = useUploadTrayStore(state => state.isExpanded);
  const expand = useUploadTrayStore(state => state.expand);
  const minimize = useUploadTrayStore(state => state.minimize);
  const dismiss = useUploadTrayStore(state => state.dismiss);
  const retryItem = useUploadTrayStore(state => state.retryItem);
  const removeItem = useUploadTrayStore(state => state.removeItem);
  const resolveCollision = useUploadTrayStore(state => state.resolveCollision);

  if (items.length === 0) {
    return null;
  }

  const activeCount = items.filter(
    i =>
      i.status === 'queued' ||
      i.status === 'requesting-url' ||
      i.status === 'uploading' ||
      i.status === 'confirming'
  ).length;
  const failedCount = items.filter(i => i.status === 'failed').length;
  const completedCount = items.filter(i => i.status === 'completed').length;
  const isUploading = activeCount > 0;

  const handleClick = () => {
    if (isExpanded) {
      minimize();
    } else {
      expand();
    }
  };

  const headerText = isUploading
    ? `Uploading ${activeCount} file${activeCount !== 1 ? 's' : ''}`
    : `${completedCount} file${completedCount !== 1 ? 's' : ''} uploaded`;

  const iconClass =
    failedCount > 0 ? 'text-content-error' : 'text-content-secondary';
  const iconSize = isMobile ? 18 : 16;

  const icon = isUploading ? (
    <Spinner size={isMobile ? 18 : 14} color="inherit" />
  ) : failedCount > 0 ? (
    <ErrorOutlineIcon size={iconSize} aria-hidden="true" />
  ) : (
    <UploadFileIcon size={iconSize} aria-hidden="true" />
  );

  const label = isUploading
    ? `Uploading ${activeCount}`
    : failedCount > 0
      ? `${failedCount} failed`
      : `${completedCount} uploaded`;

  return (
    <Popover
      open={isExpanded}
      onOpenChange={next => {
        if (!next) {
          minimize();
        }
      }}
    >
      {isMobile ? (
        <DSTooltip content={label}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              data-testid="upload-indicator"
              className={`size-8 ${iconClass}`}
              onClick={handleClick}
              aria-label={label}
            >
              <BadgeAnchor
                count={activeCount || failedCount || undefined}
                tone={failedCount > 0 ? 'error' : 'primary'}
              >
                {icon}
              </BadgeAnchor>
            </Button>
          </PopoverTrigger>
        </DSTooltip>
      ) : (
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            data-testid="upload-indicator"
            className={`${iconClass} text-xs`}
            onClick={handleClick}
          >
            {icon}
            {label}
          </Button>
        </PopoverTrigger>
      )}
      <PopoverContent
        align="end"
        className="w-[360px] overflow-hidden p-0"
        data-testid="upload-tray"
      >
        <div className="flex items-center bg-surface-raised px-4 py-2">
          <Text variant="caption" className="flex-1 font-semibold">
            {headerText}
          </Text>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={dismiss}
            aria-label="Dismiss upload tray"
          >
            <CloseIcon aria-hidden="true" className="size-4" />
          </Button>
        </div>
        <div className="max-h-[300px] overflow-auto">
          {items.map(item => (
            <UploadTrayItem
              key={item.localId}
              item={item}
              onRetry={retryItem}
              onRemove={removeItem}
              onResolveCollision={resolveCollision}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const TopAppBar: React.FC = () => {
  const currentUser = useCurrentUser();
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(
    null
  );

  // Breakpoints: md/sm mirror the old MUI queries (UploadIndicator, title
  // visibility); lg matches the DS SideNav overlay/docked switch.
  const isBelowMd = useMediaQueryMatches('(max-width: 959.95px)');
  const isBelowSm = useMediaQueryMatches('(max-width: 599.95px)');
  const isBelowLg = useMediaQueryMatches('(max-width: 1023.95px)');

  const { toggleSidebar, sidebarCollapsed, toggleDesktopSidebar } =
    useShellStore(
      useShallow(state => ({
        toggleSidebar: state.toggleSidebar,
        sidebarCollapsed: state.sidebarCollapsed,
        toggleDesktopSidebar: state.toggleDesktopSidebar,
      }))
    );

  const location = useLocation();
  const activeItem = findActiveNavItem(location.pathname);

  const handleUserMenuOpen = () => {
    const avatarElement = document.querySelector('[data-testid="user-avatar"]');
    setUserMenuAnchorEl(avatarElement as HTMLElement | null);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const ActiveIcon = activeItem?.icon;

  // Left: Sidebar toggle — hamburger on the overlay breakpoint, sidebar icon
  // on desktop when the docked column is collapsed.
  const leading = isBelowLg ? (
    <DSTooltip content="Menu" side="bottom">
      <TopBarButton
        onClick={toggleSidebar}
        data-testid="sidebar-toggle-button"
        aria-label="Menu"
      >
        <MenuIcon className="size-4" aria-hidden="true" />
      </TopBarButton>
    </DSTooltip>
  ) : sidebarCollapsed ? (
    <DSTooltip content="Open sidebar" side="bottom">
      <TopBarButton
        onClick={toggleDesktopSidebar}
        data-testid="sidebar-toggle-button"
        aria-label="Open sidebar"
      >
        <PanelLeftIcon className="size-4" aria-hidden="true" />
      </TopBarButton>
    </DSTooltip>
  ) : undefined;

  return (
    <>
      <TopBar
        leading={leading}
        titleIcon={
          ActiveIcon && !isBelowSm ? (
            <ActiveIcon className="size-4" aria-hidden="true" />
          ) : undefined
        }
        title={activeItem && !isBelowSm ? activeItem.text : undefined}
        actions={
          <>
            {/* Upload Indicator */}
            <UploadIndicator isMobile={isBelowMd} />

            {/* Notifications Icon (Placeholder) */}
            <DSTooltip content="Notifications" side="bottom">
              <TopBarButton
                data-testid="appbar-notifications-button"
                aria-label="Notifications"
              >
                <BadgeAnchor count={0} tone="primary">
                  <NotificationsIcon className="size-4.5" aria-hidden="true" />
                </BadgeAnchor>
              </TopBarButton>
            </DSTooltip>

            {/* User Avatar */}
            <UserAvatar
              initials={currentUser?.initials || undefined}
              loading={!currentUser}
              fallbackIcon={
                <PersonIcon className="size-4" aria-hidden="true" />
              }
              onClick={handleUserMenuOpen}
              tooltip="Profile"
              size="small"
              border={true}
              testId="user-avatar"
            />
          </>
        }
      />

      {/* User Menu Popover */}
      <UserMenuPopover
        anchorEl={userMenuAnchorEl}
        onClose={handleUserMenuClose}
      />
    </>
  );
};
